/**
 * EventPady+ — Onboarding Tour Overlay
 *
 * A centered modal-style overlay that walks the user through the 5-step
 * onboarding tour. Renders only when TourContext.isVisible is true.
 *
 * Design:
 *   - Backdrop blur to keep the user focused
 *   - Step indicator dots (not numbered — lower cognitive load)
 *   - Progress bar showing overall completion
 *   - Primary CTA advances the step; secondary navigates to the feature page
 *   - "Skip tour" link dismisses permanently
 *
 * HCI rationale:
 *   - Modal rather than tooltip-style: simpler, works without DOM targeting,
 *     works on any screen size, zero accessibility gymnastics.
 *   - User can skip at any step — we never trap them.
 *   - Steps are persisted server-side so returning users pick up where they left off.
 */
import { useNavigate } from 'react-router-dom'
import {
  Users, Settings2, UserPlus, MessageSquare, Sparkles, ArrowRight, X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useTour, TOUR_STEPS } from './TourContext'
import type { TourStepKey } from '@/types'

// ---------------------------------------------------------------------------
// Step icon map
// ---------------------------------------------------------------------------

const STEP_ICONS: Record<TourStepKey, React.ElementType> = {
  welcome:            Sparkles,
  register_visitor:   Users,
  configure_workflow: Settings2,
  invite_team:        UserPlus,
  explore_sms:        MessageSquare,
}

// ---------------------------------------------------------------------------
// Dot indicators
// ---------------------------------------------------------------------------

function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5" role="tablist" aria-label="Tour progress">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          role="tab"
          aria-selected={i === current}
          aria-label={`Step ${i + 1} of ${total}`}
          className={cn(
            'rounded-full transition-all duration-200',
            i === current
              ? 'h-2 w-6 bg-primary'
              : i < current
              ? 'h-2 w-2 bg-primary/40'
              : 'h-2 w-2 bg-muted-foreground/20',
          )}
        />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tour card
// ---------------------------------------------------------------------------

function TourCard() {
  const navigate = useNavigate()
  const { currentStep, currentIndex, isLastStep, completeAndNext, dismiss } = useTour()

  if (!currentStep) return null

  const Icon = STEP_ICONS[currentStep.key]
  const total = TOUR_STEPS.length

  const handleAction = async () => {
    await completeAndNext()
    if (currentStep.actionPath) {
      void navigate(currentStep.actionPath)
    }
  }

  return (
    <div
      className="relative w-full max-w-sm rounded-2xl border bg-white shadow-xl"
      role="dialog"
      aria-modal="true"
      aria-label={`Onboarding tour — step ${currentIndex + 1} of ${total}: ${currentStep.title}`}
    >
      {/* Dismiss button */}
      <button
        type="button"
        onClick={() => { void dismiss() }}
        className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label="Skip tour"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex flex-col gap-6 p-8 pt-10">
        {/* Icon */}
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10">
          <Icon className="h-7 w-7 text-primary" />
        </div>

        {/* Text */}
        <div className="space-y-2">
          <h2 className="text-lg font-semibold leading-tight tracking-tight">
            {currentStep.title}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {currentStep.description}
          </p>
        </div>

        {/* Step dots */}
        <StepDots current={currentIndex} total={total} />

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <Button
            onClick={() => { void handleAction() }}
            className="w-full"
          >
            {isLastStep ? 'Finish tour' : (currentStep.actionLabel ?? 'Next')}
            {!isLastStep && <ArrowRight className="ml-2 h-4 w-4" />}
          </Button>

          {!isLastStep && (
            <button
              type="button"
              onClick={() => { void dismiss() }}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline py-1"
            >
              Skip tour
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Overlay wrapper
// ---------------------------------------------------------------------------

export function TourOverlay() {
  const { isVisible } = useTour()

  if (!isVisible) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      aria-live="polite"
    >
      <TourCard />
    </div>
  )
}
